import React from "react";
import { OTelSpan } from "../types";

interface DefaultRunViewProps {
  span: OTelSpan;
}

const safeParseJSON = (str?: string) => {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

export const DefaultRunView: React.FC<DefaultRunViewProps> = ({ span }) => {
  // Try multiple attribute conventions for input/output
  const inputValue =
    span.attributes["input.value"] ??
    span.attributes["gen_ai.system_instructions"];
  const outputValue =
    span.attributes["output.value"] ?? span.attributes["final_result"];

  const parsedInput =
    typeof inputValue === "string" ? safeParseJSON(inputValue) : inputValue;
  const parsedOutput =
    typeof outputValue === "string" ? safeParseJSON(outputValue) : outputValue;

  // Also check for pydantic_ai.all_messages as a fallback
  const allMessages = span.attributes["pydantic_ai.all_messages"]
    ? safeParseJSON(
        typeof span.attributes["pydantic_ai.all_messages"] === "string"
          ? span.attributes["pydantic_ai.all_messages"]
          : JSON.stringify(span.attributes["pydantic_ai.all_messages"])
      )
    : null;

  const hasContent =
    inputValue !== undefined || outputValue !== undefined || allMessages;

  return (
    <div className="flex flex-col h-full bg-white p-5 space-y-6 overflow-y-auto">
      {/* Input Section */}
      {inputValue !== undefined && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
            Input
          </h4>
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md p-4 overflow-x-auto">
            <pre className="text-sm font-mono text-[#0f172a] whitespace-pre-wrap">
              {parsedInput
                ? JSON.stringify(parsedInput, null, 2)
                : String(inputValue)}
            </pre>
          </div>
        </div>
      )}

      {/* Output Section */}
      {outputValue !== undefined && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
            Output
          </h4>
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md p-4 overflow-x-auto">
            <pre className="text-sm font-mono text-[#0f172a] whitespace-pre-wrap">
              {parsedOutput
                ? JSON.stringify(parsedOutput, null, 2)
                : String(outputValue)}
            </pre>
          </div>
        </div>
      )}

      {/* Pydantic-AI all_messages fallback */}
      {!inputValue && !outputValue && Array.isArray(allMessages) && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
            Messages
          </h4>
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md p-4 overflow-x-auto">
            <pre className="text-sm font-mono text-[#0f172a] whitespace-pre-wrap">
              {JSON.stringify(allMessages, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {!hasContent && (
        <div className="text-sm text-[#64748b]">
          No input or output values recorded for this span.
        </div>
      )}
    </div>
  );
};
